import { Injectable, OnDestroy } from '@angular/core';
import { IncidentChatMessageCreated } from '../models/chat';

// Provided by each mobile dashboard so its audio resources follow the page lifetime.
@Injectable()
export class ChatAlertsService implements OnDestroy {
  private audioContext?: AudioContext;
  private interacted = false;
  private receivedMessages = new Set<string>();
  private foreground = !document.hidden;

  setForeground(foreground: boolean): void {
    this.foreground = foreground;
  }

  enable(): void {
    this.interacted = true;
    try {
      this.audioContext ??= new AudioContext();
      if (this.audioContext.state === 'suspended') {
        void this.audioContext.resume().catch(() => {});
      }
    } catch { /* Sound is optional when the browser does not support or allow it. */ }
  }

  notify(update: IncidentChatMessageCreated): void {
    this.notifyMany([update]);
  }

  notifyMany(updates: IncidentChatMessageCreated[]): void {
    // Leave background deliveries unannounced so the foreground refresh can alert for them.
    if (!this.foreground || document.hidden) return;
    let fresh = false;
    for (const update of updates) {
      const key = `${update.incidentId}:${update.chatMessageId}`;
      if (this.receivedMessages.has(key)) continue;
      this.receivedMessages.add(key);
      fresh = true;
    }
    if (!fresh || !this.interacted) return;

    try { navigator.vibrate?.(300); } catch { /* Vibration is best effort. */ }
    void this.playChime();
  }

  private async playChime(): Promise<void> {
    const context = this.audioContext;
    if (!context || context.state === 'closed') return;
    try {
      if (context.state === 'suspended') await context.resume();
      if (this.audioContext !== context || context.state !== 'running' || !this.foreground || document.hidden) return;

      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = context.currentTime;
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(660, start);
      oscillator.frequency.setValueAtTime(880, start + 0.4);
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.12, start + 0.02);
      gain.gain.linearRampToValueAtTime(0, start + 0.34);
      gain.gain.setValueAtTime(0, start + 0.4);
      gain.gain.linearRampToValueAtTime(0.12, start + 0.42);
      gain.gain.linearRampToValueAtTime(0, start + 0.8);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
      oscillator.start(start);
      oscillator.stop(start + 0.8);
    } catch { /* A blocked or interrupted sound must not interrupt chat handling. */ }
  }

  ngOnDestroy(): void {
    this.interacted = false;
    this.receivedMessages.clear();
    const context = this.audioContext;
    this.audioContext = undefined;
    if (context && context.state !== 'closed') void context.close().catch(() => {});
  }
}
